import { useState, useEffect, useCallback } from 'react'
import apiService from '@/services/api'
import { FileNote, FileNoteVisibility } from '@/types'

export interface UseFileNotesResult {
  notes: FileNote[]
  isLoading: boolean
  hasError: boolean
  addNote: (content: string, visibility: FileNoteVisibility) => Promise<FileNote>
  updateNote: (noteId: number, data: { content: string; visibility: FileNoteVisibility }) => Promise<FileNote>
  deleteNote: (noteId: number) => Promise<void>
  reload: () => void
}

export function useFileNotes(fileId: number): UseFileNotesResult {
  const [notes, setNotes] = useState<FileNote[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  const reload = useCallback(() => {
    let cancelled = false
    setIsLoading(true)
    setHasError(false)
    apiService
      .getFileNotes(fileId)
      .then(({ notes: rows }) => {
        if (!cancelled) setNotes(rows)
      })
      .catch(() => {
        if (!cancelled) setHasError(true)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [fileId])

  useEffect(() => {
    setNotes([])
    return reload()
  }, [reload])

  const addNote = useCallback(
    async (content: string, visibility: FileNoteVisibility) => {
      const { note } = await apiService.createFileNote(fileId, { content, visibility })
      setNotes(prev => [note, ...prev])
      return note
    },
    [fileId]
  )

  const updateNote = useCallback(
    async (noteId: number, data: { content: string; visibility: FileNoteVisibility }) => {
      const { note } = await apiService.updateFileNote(fileId, noteId, data)
      setNotes(prev => prev.map(n => (n.id === noteId ? note : n)))
      return note
    },
    [fileId]
  )

  const deleteNote = useCallback(
    async (noteId: number) => {
      await apiService.deleteFileNote(fileId, noteId)
      setNotes(prev => prev.filter(n => n.id !== noteId))
    },
    [fileId]
  )

  return { notes, isLoading, hasError, addNote, updateNote, deleteNote, reload }
}
